// feature/lifecycle-redesign.
// Centered modal that replaces the right-side SlidePanel for action UIs.
// Sized variants: sm (420px) / md (560px, default) / lg (760px) / xl (960px).
// Supports header (title + subtitle + tags + close), scrollable body,
// pinned footer. Esc + backdrop click + X all close.
//
// We keep the existing Modal.js around for legacy callers (Confirm,
// strict-dup block, Sprint 42/52 inline modals) — same .modal-* CSS
// classes, so styles don't collide with the new .centered-modal-*
// surface defined here. Inlining the styles into a <style> tag mounted
// while open avoids touching the global stylesheet.

import React, { useEffect } from "react";

// Sprint 56b — match the existing SlidePanel design language: frosted
// backdrop with blur(4px), the modal surface uses --glass-bg-strong +
// --glass-blur + --glass-border + --shadow-level-2 from index.css. This
// keeps the look consistent across the app: every panel that opens
// (case detail, lawyer detail, Camilo, moderación, archive, sub-action
// modals) shares the same glass treatment.
const CSS = `
.cm-backdrop {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: cm-fade var(--motion-base, .2s) var(--motion-ease, ease-out);
}
[data-theme="dark"] .cm-backdrop { background: rgba(0, 0, 0, 0.5); }
@keyframes cm-fade { from { opacity: 0; } to { opacity: 1; } }

.cm-modal {
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  color: var(--text-primary, inherit);
  border-radius: 12px;
  border: 1px solid var(--glass-border);
  box-shadow: var(--shadow-level-2);
  width: 100%;
  max-height: 90vh;
  display: flex; flex-direction: column;
  overflow: hidden;
  animation: cm-slide .22s cubic-bezier(.32,.72,.32,1);
}
@keyframes cm-slide {
  from { transform: translateY(12px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .cm-backdrop, .cm-modal { animation: none; }
}

.cm-sm { max-width: 420px; }
.cm-md { max-width: 560px; }
.cm-lg { max-width: 760px; }
.cm-xl { max-width: 960px; }

.cm-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 18px 22px;
  border-bottom: 1px solid var(--border-hairline);
  flex-shrink: 0;
  gap: 16px;
}
.cm-title-block { flex: 1; min-width: 0; }
.cm-title    { margin: 0 0 4px; font-size: 17px; font-weight: 600; color: var(--text-primary); }
.cm-subtitle { margin: 0 0 6px; font-size: 13px; color: var(--text-muted); line-height: 1.4; }
.cm-tags     { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px; }
.cm-close-btn {
  background: transparent; border: none; cursor: pointer;
  color: var(--text-muted); padding: 2px 8px; border-radius: 4px;
  font-size: 22px; line-height: 1; flex-shrink: 0;
}
.cm-close-btn:hover { background: var(--color-hover-bg); }

.cm-body {
  flex: 1; overflow-y: auto; padding: 18px 22px;
  -webkit-overflow-scrolling: touch;
}
.cm-footer {
  padding: 14px 22px;
  border-top: 1px solid var(--border-hairline);
  display: flex; gap: 10px; justify-content: flex-end;
  flex-shrink: 0;
  background: var(--bg-card, transparent);
}

/* Slide-tabs strip when nested inside a centered modal (CaseDetail
   tabs etc). Matches the original .slide-tabs styling so case-detail
   layouts port without adjustment. */
.cm-modal .slide-tabs {
  display: flex; gap: 4px;
  padding: 0 22px;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--bg-card);
  flex-shrink: 0;
}
.cm-modal .slide-tab {
  padding: 12px 14px; font-size: 13px;
  color: var(--text-muted);
  border-bottom: 2px solid transparent;
  cursor: pointer;
}
/* feature/ui-polish-motion — underline is the animated ::after bar in
   index.css; border stays transparent here so they don't double. */
.cm-modal .slide-tab.active {
  color: var(--accent-mint);
  font-weight: 500;
}
.cm-modal .slide-body { padding: 22px; overflow: auto; flex: 1; }

@media (max-width: 600px) {
  .cm-backdrop { padding: 0; align-items: stretch; }
  .cm-modal {
    max-height: 100vh; border-radius: 0; max-width: 100%;
  }
}
`;

export default function CenteredModal({
  open, onClose,
  title, subtitle, headerTags,
  children, footer,
  size = "md",
  closeOnBackdrop = true,
  // Sprint 56b — `bodyless` lets callers (CaseDetail, AbogadoCaseDetail)
  // render their own .slide-tabs + .slide-body strip directly instead of
  // the default padded .cm-body wrapper. Same modal chrome (header,
  // backdrop, glass treatment), tab-aware layout inside.
  bodyless = false,
  // Sprint 56b — extra className on the .cm-modal element. Lets the
  // abogado view force a 1100px override (.abo-cm) without touching
  // the global .cm-modal styles.
  modalClassName = "",
}) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <style>{CSS}</style>
      <div className="cm-backdrop" onClick={closeOnBackdrop ? onClose : undefined}>
        <div className={`cm-modal cm-${size}${modalClassName ? " " + modalClassName : ""}`}
             role="dialog" aria-modal="true"
             onClick={(e) => e.stopPropagation()}>
          {(title || onClose) && (
            <header className="cm-header">
              <div className="cm-title-block">
                {title && <h2 className="cm-title">{title}</h2>}
                {subtitle && <p className="cm-subtitle">{subtitle}</p>}
                {headerTags && <div className="cm-tags">{headerTags}</div>}
              </div>
              <button type="button" className="cm-close-btn"
                      onClick={onClose} aria-label="Cerrar">×</button>
            </header>
          )}
          {bodyless ? children : <div className="cm-body">{children}</div>}
          {footer && <footer className="cm-footer">{footer}</footer>}
        </div>
      </div>
    </>
  );
}
